import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { CASE_IDS, classifyHref, type CtaLocation } from "@/lib/analytics";
import { getTracker, initAnalytics, noteRouteChange } from "@/lib/analytics-runtime";
import { CONSENT_CHANGED_EVENT, getCurrentChoice } from "@/lib/consent-runtime";
import { captureAttribution, clearAttribution } from "@/lib/agency-attribution";
import { CONSENT_STORAGE_KEY, parseStoredConsent } from "@/lib/consent";

function ctaLocationOf(el: Element): CtaLocation {
  const tagged = el.closest<HTMLElement>("[data-cta-location]")?.dataset["ctaLocation"];
  if (tagged === "floating_whatsapp") return "floating_whatsapp";
  if (el.closest("nav, header:not(article header, section header)")) return "header";
  if (el.closest("footer:not(article footer, section footer)")) return "footer";
  return "body";
}

/** page_view per resolved route, one delegated click listener for contact points, case study visibility. */
export function AnalyticsTracker() {
  const router = useRouter();

  useEffect(() => {
    initAnalytics();
    let timer: number | undefined;
    let attributionPage: string | null = null;
    let attributionExpiry: number | undefined;
    // Wait a tick so the route head() has updated document.title.
    const schedulePageView = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        try {
          const choice = parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY), Date.now());
          const referrer = attributionPage ? `https://agenciaseoevolution.com${new URL(attributionPage).pathname}` : document.referrer;
          const captured = captureAttribution(window.localStorage, getCurrentChoice()?.analytics ? choice : null, window.location.href, referrer);
          attributionPage = captured ? window.location.href : null;
          window.clearTimeout(attributionExpiry);
          if (captured) {
            const expires = captured.expires_at;
            const checkExpiry = () => {
              const left = expires - Date.now();
              if (left > 0) { attributionExpiry = window.setTimeout(checkExpiry, Math.min(left, 2147483647)); return; }
              try { clearAttribution(window.localStorage); } catch { /* blocked */ }
              attributionPage = null;
            };
            checkExpiry();
          }
        } catch { /* unavailable storage: attribution stays null */ }
        getTracker()?.pageView();
      }, 60);
    };
    schedulePageView();

    const unsub = router.subscribe("onResolved", (e) => {
      // Hash-only changes are not a new page.
      if (!e.pathChanged && e.fromLocation?.searchStr === e.toLocation.searchStr) return;
      noteRouteChange();
      schedulePageView();
      observeCases();
    });

    // Consent granted now: count only the current page (no replay of earlier pages).
    const onConsent = () => schedulePageView();
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsent);

    // Never preventDefault and never wait for the network.
    const onClick = (ev: MouseEvent) => {
      const a = (ev.target as Element | null)?.closest?.("a[href]");
      if (!a) return;
      const dest = classifyHref(a.getAttribute("href"));
      if (!dest) return;
      getTracker()?.track(dest === "diagnosis" ? "cta_click" : "contact_click", {
        destination_type: dest,
        cta_location: ctaLocationOf(a),
      });
    };
    document.addEventListener("click", onClick, true);

    // view_case_study: case heading ≥25% visible for 1s, once per case per visit.
    let io: IntersectionObserver | null = null;
    const timers = new Map<Element, number>();
    const caseIds = new WeakMap<Element, (typeof CASE_IDS)[number]>();
    function observeCases() {
      io?.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
      if (typeof IntersectionObserver === "undefined") return;
      window.setTimeout(() => {
        io = new IntersectionObserver(
          (entries) => {
            for (const en of entries) {
              const id = caseIds.get(en.target);
              if (!id) continue;
              if (en.isIntersecting && en.intersectionRatio >= 0.25) {
                if (!timers.has(en.target))
                  timers.set(
                    en.target,
                    window.setTimeout(() => {
                      getTracker()?.trackOnce(`case:${id}`, "view_case_study", { case_id: id, cta_location: "case_studies" });
                    }, 1000),
                  );
              } else {
                window.clearTimeout(timers.get(en.target));
                timers.delete(en.target);
              }
            }
          },
          { threshold: [0, 0.25] },
        );
        for (const id of CASE_IDS) {
          const el = document.getElementById(`${id}-title`) ?? document.getElementById(id);
          if (el) {
            caseIds.set(el, id);
            io.observe(el);
          }
        }
      }, 100);
    }
    observeCases();

    return () => {
      unsub();
      window.clearTimeout(timer);
      window.clearTimeout(attributionExpiry);
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsent);
      document.removeEventListener("click", onClick, true);
      io?.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [router]);

  return null;
}
